import { NetworkStatus } from "@apollo/client";
import { useMemo } from "react";
import { useLocation, useSearchParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import fetchMoreUpdateQueryPreviousFix from "apollo/fetchMoreUpdateQueryPreviousFix";
import usePolledQuery from "apollo/usePolledQuery";
import { getSortOptionFromURI } from "components/Filters/helpers";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTypedContext from "hooks/useTypedContext";
import { WorkerPool } from "types/generated";
import { uniqByKey } from "utils/uniq";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  ITEMS_LIMIT,
} from "views/Account/WorkerPool/UsedByView/constants";

import { SEARCH_PRIVATE_WORKER_POOL_USED_BY } from "./gql";

const useSearchPrivateWorkerPoolUsedBy = (workerPoolId?: string) => {
  const { state: sharedPrivateWorkerPoolState }: { state: WorkerPool | undefined } = useLocation();

  const { onError } = useTypedContext(FlashContext);

  const [searchParams] = useSearchParams();
  const searchInput = getSearchQuery(searchParams);

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(searchParams, INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION),
    [searchParams]
  );

  const { error, loading, data, fetchMore, networkStatus, previousData } = usePolledQuery<{
    workerPool: WorkerPool;
  }>(SEARCH_PRIVATE_WORKER_POOL_USED_BY, {
    variables: {
      workerPoolId,
      input: {
        predicates: null,
        fullTextSearch: searchInput,
        first: ITEMS_LIMIT,
        after: null,
        ...(sortOptionFields && { orderBy: sortOptionFields }),
      },
    },
    pollingGroup: PollingIntervalGroups.Lists,
    // APOLLO CLIENT UPDATE
  });

  const [workerPool, rawSearchUsers] = useMemo(() => {
    const dataWorkerPool = data?.workerPool || previousData?.workerPool;
    if (dataWorkerPool) {
      const { searchUsers, ...workerPool } = dataWorkerPool;

      // FYI: data.workerPool could contain the huge qty of searchUsers fields
      // so we need to separate them from the workerPool object that stays to has WorkerPool type
      // TODO: implement dynamic types base on a query
      return [workerPool as WorkerPool, searchUsers];
    }

    return [undefined, undefined];
  }, [data?.workerPool, previousData?.workerPool]);

  const memoizedEntities = useMemo(() => {
    return rawSearchUsers?.edges.map((edge) => edge.node) || [];
  }, [rawSearchUsers?.edges]);

  const loadMoreItems = async () => {
    try {
      if (rawSearchUsers?.pageInfo.endCursor && rawSearchUsers?.pageInfo.hasNextPage) {
        await fetchMore({
          updateQuery: (prev, { fetchMoreResult }) => {
            const previousData = fetchMoreUpdateQueryPreviousFix(
              !!fetchMoreResult?.workerPool?.searchUsers,
              prev,
              data!
            );

            if (
              fetchMoreResult?.workerPool.searchUsers &&
              fetchMoreResult.workerPool.searchUsers.edges.length > 0
            ) {
              return {
                workerPool: {
                  ...previousData.workerPool,
                  ...fetchMoreResult.workerPool,
                  searchUsers: {
                    ...fetchMoreResult.workerPool.searchUsers,
                    edges: uniqByKey(
                      [
                        ...(previousData.workerPool.searchUsers?.edges || []),
                        ...fetchMoreResult.workerPool.searchUsers.edges,
                      ],
                      "cursor"
                    ),
                  },
                },
              };
            }

            return previousData;
          },
          variables: {
            input: {
              first: ITEMS_LIMIT,
              after: rawSearchUsers.pageInfo.endCursor,
              fullTextSearch: searchInput,
              predicates: null,
              ...(sortOptionFields && { orderBy: sortOptionFields }),
            },
          },
        });
      }
    } catch (error) {
      onError(error);
    }
  };

  const workerPoolResult = workerPool || previousData?.workerPool || sharedPrivateWorkerPoolState;

  return {
    workerPool: workerPoolResult,
    entities: memoizedEntities,
    error,
    isPageRefetching: loading && networkStatus !== NetworkStatus.loading,
    isAwaitingFreshData: !data,
    isPageEmpty: !!data && !memoizedEntities.length && !searchInput,
    hasNoFilteringResults: !!data && !memoizedEntities.length && !!searchInput,
    loadMoreItems,
    isPageLoading: loading && !workerPoolResult && networkStatus === NetworkStatus.loading,
  };
};

export default useSearchPrivateWorkerPoolUsedBy;
